import { StyleSheet, Animated, FlatList, Platform, Pressable, RefreshControl, Text, TextInput, View, useWindowDimensions, LayoutChangeEvent, ScrollView, ActivityIndicator } from 'react-native';
import React, { memo, useEffect, useRef, useState, useCallback } from 'react';
import styles from '@stylesheet';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import * as Animatable from 'react-native-animatable';
import { AntDesign, Entypo, Ionicons } from "@expo/vector-icons";
import { useLottieAnim } from '@hooks/lottieHook';
import apis from '@api/api';
import SearchComponent from '@components/Global/SearchComponent/SearchBar';
import IndividualMailComponent from './Components/IndividualMailComponent';
import { FontAwesome } from '@expo/vector-icons';
import MailModal from './Components/MailModal';
import { googleAuths } from '@reduxLocal/selectors/userSelectors';
import { Title } from '@constants/Themed';
import Markdown from "react-native-markdown-display";
import { H5, Separator, SizableText, XStack, YStack, Card } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import RenderMailItems from './Components/MailItem';
import { SectionList } from 'react-native';
import { Virtuoso } from 'react-virtuoso';

const MailScreen = ({ navigation }) => {
  const [activeFilter, setActiveFilter] = useState('INBOX');
  const [mailRetrieved, setMailRetrieved] = useState([]);
  const mailAuthenticated = useSelector((state: DefaultRootState) => state.promiseData.data.userData.authentications?.mail?.mailAuthenticated)
  const googleObject = googleAuths();
  const [data, setData] = useState([]);
  const [searchVisible, setSearchVisible] = useState(true);
  const animation = useLottieAnim();
  const [query, setQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [searchState, setSearchState] = useState('');
  const [itemSelected, setItemSelected] = useState();
  const [newVisible, setNewVisible] = useState(false);
  const [mailParams, setMailParams] = useState({ to: "", from: "", subject: "", body: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageTokens, setPageTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMailDetail, setShowMailDetail] = useState(false);
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { width } = useWindowDimensions();

  // Refs for layout measurements
  const containerRef = useRef<View>(null);

  // Dynamic layout state
  const [containerWidth, setContainerWidth] = useState(0);
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const [mailListWidth, setMailListWidth] = useState(0);

  // Responsive variables
  const isSmallScreen = width < 768;
  const isMediumScreen = width >= 768 && width < 1024;
  const isLargeScreen = width >= 1024;

  // Layout handlers
  const handleContainerLayout = useCallback((event: LayoutChangeEvent) => {
    const { width: containerW } = event.nativeEvent.layout;
    setContainerWidth(containerW);
    console.log('Container width:', containerW);
  }, []);

  const calculateMailListWidth = useCallback((containerW: number, sidebarW: number) => {
    if (containerW <= 0 || sidebarW <= 0) return 0;

    const gap = isSmallScreen ? 12 : 16; // Actual gap from container style
    const padding = isSmallScreen ? 24 : 32; // Account for container padding
    const remainingWidth = containerW - sidebarW - gap - padding;
    return Math.max(remainingWidth, 300); // Minimum width of 300px
  }, [isSmallScreen]);

  const handleSidebarLayout = useCallback((event: LayoutChangeEvent) => {
    const { width: sidebarW } = event.nativeEvent.layout;
    setSidebarWidth(sidebarW);
    console.log('Sidebar width:', sidebarW, 'Container width:', containerWidth);

    // Calculate remaining width for mail list based on actual view dimensions
    const calculatedWidth = calculateMailListWidth(containerWidth, sidebarW);
    if (calculatedWidth > 0) {
      setMailListWidth(calculatedWidth);
      console.log('Calculated mail list width:', calculatedWidth);
    }
  }, [containerWidth, calculateMailListWidth]);

  const handleMailListLayout = useCallback((event: LayoutChangeEvent) => {
    const { width: mailW } = event.nativeEvent.layout;
    console.log('Actual mail list width:', mailW);
    // Use actual width if it's reasonable, otherwise use calculated
    if (mailW > 200 && mailW < containerWidth) {
      setMailListWidth(mailW);
    }
  }, [containerWidth]);

  // Recalculate mail list width when container or sidebar dimensions change
  useEffect(() => {
    if (containerWidth > 0 && sidebarWidth > 0) {
      const calculatedWidth = calculateMailListWidth(containerWidth, sidebarWidth);
      if (calculatedWidth > 0 && calculatedWidth !== mailListWidth) {
        setMailListWidth(calculatedWidth);
      }
    }
  }, [containerWidth, sidebarWidth, calculateMailListWidth, mailListWidth]);

  // Trigger animation when mail item is selected
  useEffect(() => {
    console.log('itemSelected changed:', itemSelected ? 'item selected' : 'none');
    if (itemSelected) {
      setShowMailDetail(true);
    } else {
      // Delay hiding to allow slide out animation
      const timer = setTimeout(() => setShowMailDetail(false), 300);
      return () => clearTimeout(timer);
    }
  }, [itemSelected]);

  const groupMailsByDate = (mails) => {
    if (!mails || !Array.isArray(mails) || mails.length === 0) {
      return [];
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
    
    const sections = {
      today: [],
      thisWeek: [],
      lastWeek: [],
      more: []
    };

    mails.forEach(mail => {
      const dateHeader = mail?.payload?.headers?.find(header => header.name === 'Date');
      if (!dateHeader || !dateHeader.value) {
        // If no date, put in 'more' section
        sections.more.push(mail);
        return;
      }

      const mailDate = new Date(dateHeader.value);
      if (isNaN(mailDate.getTime())) {
        // If invalid date, put in 'more' section
        sections.more.push(mail);
        return;
      }

      mailDate.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
      const diffTime = today.getTime() - mailDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        sections.today.push(mail);
      } else if (diffDays > 0 && diffDays <= 7) {
        sections.thisWeek.push(mail);
      } else if (diffDays > 7 && diffDays <= 14) {
        sections.lastWeek.push(mail);
      } else {
        sections.more.push(mail);
      }
    });

    return [
      { title: 'Today', data: sections.today },
      { title: 'This Week', data: sections.thisWeek },
      { title: 'Last Week', data: sections.lastWeek },
      { title: 'More', data: sections.more }
    ].filter(section => section.data.length > 0); // Only return sections with data
  };

  const sections = React.useMemo(() => {
    return groupMailsByDate(mailRetrieved);
  }, [mailRetrieved]);
  
  //Retrieve mail when filter changes
  useEffect(() => {
    if (mailAuthenticated) {
      onRefresh()
    } 
  }, [activeFilter]);


  const onRefresh = React.useCallback(() => {
    setRefreshing(true);
    setLoading(true);
    handleGetMail()
      .then(() => {
        setRefreshing(false);
        setLoading(false);
      })
      .catch(() => {
        setRefreshing(false);
        setLoading(false);
      });
  }, [activeFilter]);

  useEffect(() => {
    if (mailAuthenticated) {
      onRefresh()
    }
    setData(mailRetrieved)
  }, [data]);


  const handleGetMail = React.useCallback(async () => {
    try {
      const res = await apis.getAllGmail(googleObject?.googleId, googleObject?.accessToken, activeFilter);
      setMailRetrieved(res.messages);
      setTotalPages(res.estimateSize);
      setPageTokens(tokens => [...tokens, res.nextPageToken]);
    } catch (error) {
      console.error('Failed to get mail:', error);
      // The interceptor will handle token refresh and retry
      // If it still fails after retry, the error modal will be shown by the interceptor
    }
  }, [googleObject?.googleId, googleObject?.accessToken, activeFilter]);

  const handleNextPage = async () => {
    if(currentPage >= totalPages) return;
    setLoading(true);
    try {
      let nextPageToken = pageTokens[pageTokens.length - 1];
      const res = await apis.requestNextPage(googleObject?.accessToken, nextPageToken);
      setCurrentPage(currentPage + 1);
      setMailRetrieved(res.messages);
      setPageTokens(tokens => [...tokens, res.nextPageToken]);
    } finally {
      setLoading(false);
    }
  }

  const handleLastPage = async () => {
    if(currentPage === 1) return;
    setLoading(true);
    try {
      let nextPageToken = pageTokens[pageTokens.length - 2];
      const res = await apis.requestLastPage(googleObject?.accessToken, nextPageToken);
      setCurrentPage(currentPage - 1);
      setMailRetrieved(res.messages);
    } finally {
      setLoading(false);
    }
  }

  const handleQuery = async (query) => {
    setLoading(true);
    try {
      const res = await apis.searchGmail(googleObject?.accessToken, query || searchState);
      setMailRetrieved(res);
    } finally {
      setLoading(false);
    }
  }

  const _handleReply = (mail) => {

    const subject = mail.payload?.headers.find(subject => subject.name === 'Subject');
    const sender = mail.payload?.headers.find(sender => sender.name === 'From');
    const body = mail.payload?.headers.find(body => body.name === 'Body');
    const to = mail.payload?.headers.find(to => to.name === 'To');

    setMailParams({ from: sender, to: to, subject: subject, body: body });
    setNewVisible(true);
  }

  const _handleForward = (mail) => {

    const subject = mail.payload?.headers.find(subject => subject.name === 'Subject');
    const sender = mail.payload?.headers.find(sender => sender.name === 'From');
    const body = mail.payload?.headers.find(body => body.name === 'Body');

    setMailParams({ from: sender, to: "", subject: subject, body: body });
    setNewVisible(true);
  }

  const _handleMarking = async (mail) => {
    //Mark mail as read
    if (mail.labelIds.includes('UNREAD')) {
      await apis.markRead(mail.id, googleObject?.accessToken).then(res => {
        //Mark mail as read
        setMailRetrieved(mailRetrieved.map(item => {
          if (item.id === mail.id) {
            item.labelIds = item.labelIds.filter(label => label !== 'UNREAD')
          }
          return item;
        }))
      })
    } else return;
  }



  return ( 
    <View style={{ 
      flex: 1, 
      paddingVertical: Platform.select({ web: isSmallScreen ? 12 : 20, default: 15 }),
      paddingHorizontal: Platform.select({ web: isSmallScreen ? 12 : 20, default: 10 }),
      backgroundColor: theme ? "#f8f9fa" : "#121212"
    }}>
        <SearchComponent 
        customStyle={{
          paddingLeft: isSmallScreen ? 12 : 20,
          paddingRight: isSmallScreen ? 12 : 20
        }} 
          searchState={searchState} 
          setSearchState={setSearchState} 
          customIcons={[
            { functionName: 'newmail', function: () => setNewVisible(true), icon: <Entypo name="new-message" size={20} color="white" /> }
          ]} 
          callback={async (q) => handleQuery(q)} 
      />
      <MailModal visible={newVisible} setVisible={setNewVisible} sendTo={mailParams.to} sendFrom={mailParams.from} setBody={mailParams.body} sendSubject={mailParams.subject} />
      <View
        ref={containerRef}
        style={{
          flexDirection: isSmallScreen ? 'column' : 'row',
          gap: isSmallScreen ? 12 : 16,
          flex: 1,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}
        onLayout={handleContainerLayout}
      >
        {
          Platform.OS === 'web' && !isSmallScreen &&
          <View
            style={{
              flex: itemSelected ? 0.05 : (isMediumScreen ? 0.2 : 0.1),
              paddingVertical: isSmallScreen ? 8 : 12,
              backgroundColor: theme ? "#ffffff" : "#1a1a1a",
              borderRadius: 12,
                padding: isSmallScreen ? 12 : (itemSelected ? 8 : 16),
                marginRight: isSmallScreen ? 0 : 12,
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 8,
              elevation: 3,
              transform: [{ scale: itemSelected ? 0.95 : 1 }],
                opacity: itemSelected ? 0.7 : 1,
                minWidth: itemSelected ? 200 : (isSmallScreen ? '100%' : 280),
                maxWidth: itemSelected ? 220 : (isMediumScreen ? 320 : undefined)
              }}
              onLayout={handleSidebarLayout}
            >
          <Pressable onPress={() => setNewVisible(true)} style={{ 
            ...styles.centerEverything, 
                width: "100%", 
            alignSelf: "stretch", 
            flexDirection: "row", 
                backgroundColor: theme ? "#3b82f6" : "#1d4ed8",
                padding: isSmallScreen ? 12 : (itemSelected ? 12 : 16), 
            borderRadius: 12, 
                marginBottom: isSmallScreen ? 12 : (itemSelected ? 16 : 20),
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.2,
            shadowRadius: 4,
            elevation: 2
          }}>
                <Entypo name="new-message" style={{ alignSelf: "center", flex: itemSelected ? 0 : 1 }} size={isSmallScreen ? 18 : (itemSelected ? 20 : 24)} color="white" />
                {!itemSelected && (
                  <Animatable.Text
                    animation="fadeIn"
                    duration={200}
                    style={{
                      fontWeight: "600",
                      color: "white",
                      marginLeft: 8,
                      fontSize: isSmallScreen ? 14 : 16
                    }}
                  >
                    Compose
                  </Animatable.Text>
                )}
          </Pressable>
          <View style={{ marginTop: 8 }}>
            <FlatList
              data={[
                { key: 'INBOX', icon: 'inbox', label: 'Inbox', count: 800 },
                { key: 'STARRED', icon: 'star', label: 'Starred', count: 250 },
                { key: 'SENT', icon: 'upload', label: 'Sent', count: 80 },
                    { key: 'DRAFT', icon: 'file', label: 'Draft', count: 50 },
                { key: 'SPAM', icon: 'warning', label: 'Spam', count: 30 },
                { key: 'BIN', icon: 'delete', label: 'Bin', count: 20 },
              ]}
              renderItem={({ item }) => (
                <Pressable onPress={() => setActiveFilter(item.key)} style={[MailStyle.button, {
                  alignSelf: "stretch",
                  minHeight: isSmallScreen ? 44 : (itemSelected ? 44 : 50),
                  flex: 1
                }]}>
                  <Animatable.View useNativeDriver transition={'backgroundColor'} style={[styles.centerEverything, { 
                    flex: 1, 
                    flexDirection: "row", 
                    alignItems: "center", 
                    justifyContent: itemSelected ? "center" : "flex-start",
                    padding: isSmallScreen ? 10 : (itemSelected ? 8 : 12),
                    borderRadius: 10, 
                    backgroundColor: activeFilter === item.key ? (theme ? "#3b82f6" : "#1d4ed8") : (theme ? "#f8f9fa" : "#2c2c2c"), 
                    marginBottom: 3,
                    gap: itemSelected ? 0 : 8,
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 1 },
                    shadowOpacity: activeFilter === item.key ? 0.2 : 0.05,
                    shadowRadius: 3,
                    elevation: activeFilter === item.key ? 2 : 1
                  }]}>
                    <AntDesign
                      name={item.icon as any}
                      size={isSmallScreen ? 16 : (itemSelected ? 18 : 20)}
                      color={activeFilter === item.key ? "white" : (theme ? "#495057" : "#ffffff")}
                    />
                    {!itemSelected && (
                      <Animatable.View
                        animation="fadeIn"
                        duration={200}
                        style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}
                      >
                        <Text style={{ 
                          flex: 1, 
                          marginLeft: 4, 
                          fontWeight: "600",
                          color: activeFilter === item.key ? "white" : (theme ? "#212529" : "#ffffff"),
                          fontSize: isSmallScreen ? 12 : 14
                        }}>
                          {item.label}
                        </Text>
                        <View style={{
                          backgroundColor: activeFilter === item.key
                            ? "rgba(255,255,255,0.25)"
                            : (theme ? "#e9ecef" : "#404040"),
                          paddingHorizontal: isSmallScreen ? 6 : 8,
                          paddingVertical: 2,
                          borderRadius: 10,
                          minWidth: isSmallScreen ? 20 : 24
                        }}>
                          <Text style={{ 
                            fontWeight: "600",
                            textAlign: "center",
                            color: activeFilter === item.key ? "white" : (theme ? "#6c757d" : "#cccccc"),
                            fontSize: isSmallScreen ? 10 : 11
                          }}>
                            {item.count}
                          </Text>
                        </View>
                      </Animatable.View>
                    )}
                  </Animatable.View>
                </Pressable>
              )}
              keyExtractor={item => item.key}
            />
            <View style={{ marginTop: 24, padding: 16, backgroundColor: theme ? "#f8f9fa" : "#2c2c2c", borderRadius: 12 }}>
              <Text style={{ 
                fontWeight: "600", 
                color: theme ? "#007bff" : "#4dabf7", 
                marginBottom: 16, 
                fontSize: 14,
                letterSpacing: 0.5
              }}>LABELS</Text>
              <FlatList
                data={[
                  { key: 'Personal', color: '#007bff' },
                  { key: 'Social', color: '#e91e63' },
                  { key: 'Promotions', color: '#28a745' },
                  { key: 'Business', color: '#ffc107' },
                ]}
                renderItem={({ item }) => (
                  <Pressable style={{ 
                    flexDirection: "row", 
                    alignItems: "center", 
                    marginBottom: 12,
                    padding: 8,
                    borderRadius: 8,
                    backgroundColor: theme ? "#ffffff" : "#3c3c3c"
                  }}>
                    <View style={{ 
                      width: 10, 
                      height: 10, 
                      backgroundColor: item.color, 
                      borderRadius: 5, 
                      marginRight: 12 
                    }} />
                    <SizableText style={{ 
                      fontWeight: "500", 
                      color: theme ? "#495057" : "#ffffff",
                      fontSize: 14
                    }}>{item.key}</SizableText>
                  </Pressable>
                )}
                keyExtractor={item => item.key}
              />
            </View>
          </View>
          </View>
        }
        {!itemSelected && (
          <View
            style={{
              flex: isSmallScreen ? 1 : 5,
              backgroundColor: theme ? "#ffffff" : "#1a1a1a",
              borderRadius: 16,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.12,
              shadowRadius: 12,
              elevation: 6,
              overflow: 'hidden',
              borderWidth: 1,
              borderColor: theme ? "#e9ecef" : "#2c2c2c",
              minWidth: isSmallScreen ? '100%' : undefined,
              maxWidth: '100%',
              ...(mailListWidth > 0 && { maxWidth: mailListWidth })
            }}
            onLayout={handleMailListLayout}
          >
            <View style={{ 
              flexDirection: 'row', 
              backgroundColor: theme ? "#f8f9fa" : "#2c2c2c",
              shadowRadius: 20, 
              shadowOpacity: .2, 
            borderTopStartRadius: 16,
            borderTopEndRadius: 16,
              padding: isSmallScreen ? 12 : (itemSelected ? 16 : 20),
              paddingBottom: isSmallScreen ? 8 : (itemSelected ? 12 : 15),
              borderBottomWidth: 1,
              borderBottomColor: theme ? "#e9ecef" : "#3c3c3c"
            }}>
            <Pressable
              style={({ pressed }) => [{
                flex: 1,
                padding: isSmallScreen ? 6 : 8,
                borderRadius: 8,
                backgroundColor: pressed ? (theme ? "#e9ecef" : "#404040") : (theme ? "#ffffff" : "#3c3c3c"),
                transform: [{ scale: pressed ? 0.95 : 1 }]
              }]}
              onPress={() => onRefresh()}
            >
                <FontAwesome name="refresh" size={isSmallScreen ? 16 : 18} color={theme ? "#495057" : "#ffffff"} style={{ textAlign: 'center' }} />
              </Pressable>
              <Title style={{ 
                flex: 3, 
                textAlign: 'center', 
                fontSize: isSmallScreen ? 14 : (itemSelected ? 16 : 18),
              fontWeight: '700',
                color: theme ? "#212529" : "#ffffff",
                alignSelf: 'center'
            }}>
              {activeFilter}
            </Title>
              <View style={{ 
                flex: 2, 
                gap: isSmallScreen ? 4 : 6, 
                flexDirection: 'row', 
                alignItems: 'center',
                backgroundColor: theme ? "#ffffff" : "#3c3c3c",
              borderRadius: 10,
                padding: isSmallScreen ? 4 : 6,
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 1 },
              shadowOpacity: 0.05,
              shadowRadius: 2,
              elevation: 1
              }}>
              <Pressable
                style={({ pressed }) => [{
                  flex: 1,
                  padding: isSmallScreen ? 6 : 8,
                  borderRadius: 6,
                  backgroundColor: pressed ? (theme ? "#e9ecef" : "#404040") : "transparent",
                  transform: [{ scale: pressed ? 0.95 : 1 }]
                }]}
                onPress={async () => handleLastPage()}
              >
                  <Entypo name="chevron-left" size={isSmallScreen ? 16 : 18} color={theme ? "#495057" : "#ffffff"} style={{ textAlign: 'center' }} />
                </Pressable>
                <View style={{ flex: 2, paddingVertical: isSmallScreen ? 2 : 4 }}>
                  <SizableText selectable={false} style={{ 
                    fontSize: isSmallScreen ? 10 : 11,
                  fontWeight: '600',
                    textAlign: 'center',
                    color: theme ? "#6c757d" : "#cccccc"
                  }}>{currentPage} of {totalPages}</SizableText>
                </View>
              <Pressable
                style={({ pressed }) => [{
                  flex: 1,
                  padding: isSmallScreen ? 6 : 8,
                  borderRadius: 6,
                  backgroundColor: pressed ? (theme ? "#e9ecef" : "#404040") : "transparent",
                  transform: [{ scale: pressed ? 0.95 : 1 }]
                }]}
                onPress={async () => handleNextPage()}
              >
                  <Entypo name="chevron-right" size={isSmallScreen ? 16 : 18} color={theme ? "#495057" : "#ffffff"} style={{ textAlign: 'center' }} />
                </Pressable>
              </View>
            </View>
            <ScrollView
              style={{ 
                flex: 1,
                backgroundColor: "transparent",
                width: '100%',
                maxWidth: '100%'
              }}
              showsVerticalScrollIndicator={true}
              showsHorizontalScrollIndicator={false}
              refreshControl={
                <RefreshControl
                  refreshing={refreshing}
                  onRefresh={onRefresh}
                  tintColor={theme ? "#3b82f6" : "#60a5fa"}
                />
              }
            >
              {/* Sections with Headers and Mail Items */}
              {sections.length > 0 ? (
                sections.map((section) => (
                  <View key={section.title} style={{ marginBottom: isSmallScreen ? 12 : 16 }}>
                    {/* Section Header */}
                    <View style={{ 
                      padding: isSmallScreen ? 8 : 12,
                      backgroundColor: theme ? "#3b82f6" : "#1d4ed8",
                      marginHorizontal: 16,
                      marginTop: isSmallScreen ? 8 : 12,
                      marginBottom: isSmallScreen ? 6 : 8,
                      borderRadius: 6,
                      shadowColor: "#000",
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.1,
                      shadowRadius: 2,
                      elevation: 1
                    }}>
                      <Text style={{
                        fontWeight: '600',
                        color: "white",
                        fontSize: isSmallScreen ? 12 : 14,
                        letterSpacing: 0.5
                      }}>{section.title}</Text>
                    </View>

                    {/* Mail Items in this Section */}
                    {section.data.map((item, index) => (
                      <View
                        key={item.id || `${section.title}-${index}`}
                        style={{
                          minHeight: isSmallScreen ? 80 : 90,
                          paddingHorizontal: 16,
                          borderBottomWidth: 1,
                          borderBottomColor: theme ? "#e9ecef" : "#3c3c3c",
                          width: '100%',
                          maxWidth: '100%'
                        }}
                      >
                        <RenderMailItems
                          itemSelected={itemSelected}
                          _handleMarking={_handleMarking}
                          setItemSelected={setItemSelected}
                          item={item}
                          index={index}
                          {...({ compactMode: isSmallScreen } as any)}
                        />
                      </View>
                    ))}
                  </View>
                ))
              ) : (
                  <View style={[styles.centerEverything, { padding: 40, gap: 16 }]}>
                    {loading && (
                      <ActivityIndicator
                        size="large"
                        color={theme ? "#3b82f6" : "#60a5fa"}
                      />
                    )}
                  <Text style={{
                    color: theme ? "#6c757d" : "#cccccc",
                    fontSize: 16,
                    fontWeight: "500"
                  }}>{loading ? "Loading emails..." : "No emails found"}</Text>
                </View>
              )}
            </ScrollView>
          </View>
        )}
        <Animatable.View 
          animation={itemSelected ? "slideInRight" : showMailDetail ? "slideOutRight" : undefined}
          duration={itemSelected ? 400 : 250}
          easing={itemSelected ? "ease-out-back" : "ease-in"}
          useNativeDriver={false}
          style={{ 
            opacity: itemSelected ? 1 : 0, 
            flex: itemSelected ? 1 : 0.0001,
            overflow: 'visible', 
            paddingRight: Platform.select({ web: isSmallScreen ? 20 : 40, default: 20 }),
            backgroundColor: theme ? "#ffffff" : "#1a1a1a",
            borderRadius: 16,
            marginLeft: isSmallScreen ? 0 : (itemSelected ? 12 : -350),
            marginTop: isSmallScreen && itemSelected ? 12 : 0,
            shadowColor: "#000",
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: itemSelected ? 0.15 : 0,
            shadowRadius: 16,
            elevation: itemSelected ? 8 : 0,
            transform: [
              { translateX: isSmallScreen ? 0 : (itemSelected ? 0 : 300) },
              { scale: itemSelected ? 1 : 0.9 }
            ],
            borderWidth: 1,
            borderColor: theme ? "#e9ecef" : "#2c2c2c"
          }}>
          {itemSelected && (
            <>
              {Platform.OS === 'web' && !isSmallScreen && (
                <Animatable.View
                  animation="fadeInDown"
                  duration={300}
                  delay={200}
                  style={[styles.centerEverything, {
                    position: 'absolute', 
                    right: isMediumScreen ? 16 : 20,
                    top: isMediumScreen ? 16 : 20,
                    padding: isMediumScreen ? 12 : 16,
                    backgroundColor: theme ? 'rgba(255,255,255,0.95)' : 'rgba(44,44,44,0.95)',
                    minWidth: isMediumScreen ? 50 : 60, 
                    borderRadius: 16,
                    gap: isMediumScreen ? 8 : 12,
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 8 },
                    shadowOpacity: 0.2,
                    shadowRadius: 16,
                    elevation: 12,
                    borderWidth: 1,
                    borderColor: theme ? "rgba(233,236,239,0.8)" : "rgba(60,60,60,0.8)",
                    zIndex: 100,
                    backdropFilter: 'blur(10px)'
                  }]}
                >
                  <Pressable
                    style={({ pressed }) => [{
                      padding: isMediumScreen ? 10 : 12,
                      borderRadius: 12,
                      backgroundColor: pressed ? (theme ? "#fee2e2" : "#4a1a1a") : (theme ? "#fef2f2" : "#3c2c2c"),
                      transform: [{ scale: pressed ? 0.92 : 1 }],
                      shadowColor: "#dc3545",
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: pressed ? 0.3 : 0.1,
                      shadowRadius: 4,
                      elevation: pressed ? 4 : 2
                    }]}
                    onPress={() => setItemSelected(undefined)}
                  >
                    <Ionicons name="close-circle-sharp" size={isMediumScreen ? 20 : 24} color={theme ? "#dc3545" : "#ff6b6b"} />
                  </Pressable>
                  <Pressable
                    style={({ pressed }) => [{
                      padding: isMediumScreen ? 10 : 12,
                      borderRadius: 12,
                      backgroundColor: pressed ? (theme ? "#e3f2fd" : "#1a3a4a") : (theme ? "#f8f9fa" : "#3c3c3c"),
                      transform: [{ scale: pressed ? 0.95 : 1 }]
                    }]}
                    onPress={() => _handleReply(itemSelected)}
                  >
                    <Ionicons name="arrow-back-circle" size={isMediumScreen ? 20 : 24} color={theme ? "#007bff" : "#4dabf7"} />
                  </Pressable>
                  <Pressable
                    style={({ pressed }) => [{
                      padding: isMediumScreen ? 10 : 12,
                      borderRadius: 12,
                      backgroundColor: pressed ? (theme ? "#e8f5e9" : "#1a4a1a") : (theme ? "#f8f9fa" : "#3c3c3c"),
                      transform: [{ scale: pressed ? 0.95 : 1 }]
                    }]}
                    onPress={() => _handleForward(itemSelected)}
                  >
                    <Ionicons name="arrow-forward-circle" size={isMediumScreen ? 20 : 24} color={theme ? "#28a745" : "#51cf66"} />
                  </Pressable>
                </Animatable.View>
              )}
              <Animatable.View
                animation="fadeIn"
                duration={300}
                delay={100}
                style={{
                  flex: 1,
                  padding: isSmallScreen ? 16 : 24,
                  paddingTop: Platform.OS === 'web' ? (isSmallScreen ? 20 : 80) : 24
                }}
              >
                <IndividualMailComponent 
                  handleMarked={_handleMarking} 
                  mailSelected={itemSelected} 
                  tokenAccess={googleObject?.accessToken} 
                  style={{ flex: 1 }} 
                />
              </Animatable.View>
            </>
          )}
        </Animatable.View>
      </View>
    </View>
  )
}

const MailStyle = StyleSheet.create({
  button: {
    flex: 1,
    flexDirection: "row",
    alignSelf: "stretch",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 2
  },
  buttonTextStyle: {
    fontWeight: "600",
    fontSize: 14
  },
  container: {
    flex: 1,
    backgroundColor: "#f8f9fa"
  },
  sidebar: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3
  },
  mailList: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3
  },
  headerContainer: {
    backgroundColor: "#f8f9fa",
    borderBottomWidth: 1,
    borderBottomColor: "#e9ecef",
    paddingVertical: 16,
    paddingHorizontal: 20
  }
});

export default MailScreen;